import { CartItem, IngredientSection, wouldExceedMax } from '@lib/entities/Cart';
import { Customization, MenuItem } from '@lib/entities/Menu';
import { type ApplicationState, useApplicationState } from '@lib/state';

const selectCustomization = (s: ApplicationState) => s.customization;

export const useCustomization = () => useApplicationState(selectCustomization);

/**
 * JSAD-3602: whether one more of this ingredient can be added without tripping
 * a max rule. Reads the customization slice and reuses wouldExceedMax so the
 * regular ingredient tiles hide their "+" at the ceiling — matching how the
 * Market Plate tiles already gate via showAdd. Living here means every render
 * site of the regular tile (grid, native list, current-items) is gated without
 * threading a prop through each caller.
 */
export const useCanAddIngredient = (customization: Customization): boolean => {
    const { draftCustomizations, categories, draftItem } = useCustomization();
    return !wouldExceedMax(
        draftCustomizations,
        customization,
        categories,
        draftItem?.menuItem.id,
        draftItem?.menuItem.baselineCustomizations || []
    );
};

export const mpInstructions = (section: IngredientSection) => {
    if (section.category.type === 'bases') {
        return 'Remove up to one base';
    }

    if (section.category.type === 'proteins') {
        return 'Remove or substitute up to one option';
    }

    if (section.category.type === 'essentials') {
        return 'Remove up to one ingredient from either Essentials or Premiums';
    }

    if (section.category.type === 'premiums') {
        return 'Remove up to one ingredient from either Essentials or Premiums';
    }

    if (section.category.type === 'cheese') {
        return 'Add or substitute up to 1';
    }

    if (section.category.type === 'dressings') {
        return 'Customize your dressings';
    }

    return null;
};

export const byoMpInstructions = (section: IngredientSection) => {
    if (section.category.type === 'bases') {
        return 'Please select 1';
    }

    if (section.category.type === 'proteins') {
        return 'Please select 1';
    }

    if (section.category.type === 'curated-sides') {
        return 'Select up to 4 ingredients. Combos are 2 ingredients each.';
    }

    if (section.category.type === 'garnish') {
        return 'Optional: Select up to 1';
    }

    if (section.category.type === 'dressings') {
        return 'Dressing Drizzle';
    }

    // Fallback for any other categories
    return '';
};

export const checkByoMarketPlateCustomization = (
    item: Customization,
    draftCustomizations: Customization[],
    draftItem: CartItem<MenuItem> | undefined
) => {
    const isActive = !!item.quantity;
    const category = item.ingredient.category;

    // Count current active ingredients in each category
    const activeCustomizations = draftCustomizations.filter((c) => c.quantity > 0);

    // Count active ingredients by category
    const activeBases = activeCustomizations.filter(
        (c) => c.ingredient.category === 'bases'
    ).length;
    const activeProteins = activeCustomizations.filter(
        (c) => c.ingredient.category === 'proteins'
    ).length;
    const activeGarnish = activeCustomizations.filter(
        (c) => c.ingredient.category === 'garnish'
    ).length;

    // Count total sides (combos count as 2, regular curated sides count as 1)
    const activeCuratedSides = activeCustomizations.filter(
        (c) => c.ingredient.category === 'curated-sides'
    );
    const activeCombos = activeCuratedSides.filter((c) => c.ingredient.is_combo === true).length;
    const activeRegularSides = activeCuratedSides.filter((c) => !c.ingredient.is_combo).length;
    const totalActiveSides = activeCombos * 2 + activeRegularSides;

    // BYO Market Plate limits
    const limits = {
        bases: 1,
        proteins: 1,
        garnish: 1,
        dressings: 1, // First one is free, additional ones cost extra
        totalSides: 4 // Combination of curated combos (2 each) and curated sides (1 each)
    };

    let showAdd = false;
    let showRemove = false;

    switch (category) {
        case 'bases':
            showAdd = !isActive && activeBases < limits.bases;
            showRemove = isActive;
            break;

        case 'proteins':
            showAdd = !isActive && activeProteins < limits.proteins;
            showRemove = isActive;
            break;

        case 'curated-sides': {
            // Combos count as 2 sides, regular curated sides count as 1 side
            const isCombo = item.ingredient.is_combo === true;
            const sidesCount = isCombo ? 2 : 1;
            const sidesIfAdded = totalActiveSides + (isActive ? 0 : sidesCount);
            showAdd = !isActive && sidesIfAdded <= limits.totalSides;
            showRemove = isActive;
            break;
        }

        case 'garnish':
            showAdd = !isActive && activeGarnish < limits.garnish;
            showRemove = isActive;
            break;

        case 'dressings':
            // Dressings work like normal - first one free, additional ones cost extra
            showAdd = !isActive; // Can always add more dressings (they'll cost extra after the first)
            showRemove = isActive;
            break;

        default:
            // For any other categories, default behavior
            showAdd = !isActive;
            showRemove = isActive;
            break;
    }

    return {
        showRemove,
        showAdd,
        isActive
    };
};

export const checkMarketPlateCustomization = (
    item: Customization,
    draftCustomizations: Customization[],
    draftItem: CartItem<MenuItem> | undefined
) => {
    const isActive = !!item.quantity;
    const isPremiumOrEssential =
        item.ingredient.category === 'premiums' || item.ingredient.category === 'essentials';

    // Get baseline count for this category (or both categories for premiums/essentials)
    const baselineCustomizations = draftItem?.menuItem?.baselineCustomizations?.filter((bc) =>
        isPremiumOrEssential
            ? bc.ingredient.category === 'premiums' || bc.ingredient.category === 'essentials'
            : bc.ingredient.category === item.ingredient.category
    );

    let baselineCount = baselineCustomizations?.length || 0;

    // Ensure baseline count is at least 1 for cheese category
    if (item.ingredient.category === 'cheese') {
        baselineCount = Math.max(1, baselineCount);
    }

    // Count active customizations in this category (or both categories for premiums/essentials)
    const currentActiveCount = draftCustomizations.filter((customization) => {
        if (isPremiumOrEssential) {
            return (
                (customization.ingredient.category === 'premiums' ||
                    customization.ingredient.category === 'essentials') &&
                !!customization.quantity
            );
        }
        return (
            customization.ingredient.category === item.ingredient.category &&
            !!customization.quantity
        );
    }).length;

    // Only allow removal if the difference between baseline and active count is 0
    // and the item is active
    const showRemove = isActive && Math.abs(baselineCount - currentActiveCount) < 1;
    const hasActiveInCategory = currentActiveCount >= baselineCount;
    const showAdd = !showRemove && !hasActiveInCategory && !isActive;

    return {
        showRemove,
        showAdd,
        isActive
    };
};

export default useCustomization;
